import React, { useEffect, useState } from 'react';
import { CartaStatus, ICartaCircularizacao, IRegistroCircularizacao } from '../interfaces/ICircularizacao';
import { CircularizacaoService } from '../services/CircularizacaoService';
import { justificativaPadraoConclusao } from '../utils/justificativasPadrao';

interface Props {
  registro: IRegistroCircularizacao;
  onFechar: () => void;
}

const LABEL_STATUS: Record<CartaStatus, string> = {
  AGUARDANDO_PREPARACAO: 'Aguardando preparação',
  PRONTA: 'Pronta para aprovação',
  APROVADA: 'Aprovada',
  ENVIO_AGENDADO: 'Envio agendado',
  ENVIADA: 'Enviada',
  FALHA_ENVIO: 'Falha no envio',
  AGUARDANDO_RESPOSTA: 'Aguardando resposta',
  A_VENCER: 'Prazo a vencer',
  VENCIDA: 'Prazo vencido',
  RESPOSTA_RECEBIDA: 'Resposta recebida',
  EM_ANALISE: 'Resposta em análise',
  CONCORDANTE: 'Resposta concordante',
  DIVERGENTE: 'Resposta divergente',
  PARCIAL: 'Resposta parcial',
  INCONCLUSIVA: 'Resposta inconclusiva',
  RECUSA: 'Recusa de resposta',
  PROCEDIMENTO_ALTERNATIVO: 'Procedimento alternativo',
  CONCLUIDA: 'Procedimento concluído',
  CANCELADA: 'Cancelada',
};

// Card 08/09/12 do Épico Backend — status real da carta (19 estados) além do que o Front-End já
// mostrava localmente (situacaoPreparacaoCarta, de 4 estados). Classificar a resposta (Card 11/12)
// acontece em RespostasPendentesModal; aqui só se aprova o envio e, depois de classificada, se
// conclui o procedimento (RN-010 — concluir é sempre uma ação separada e explícita).
const STATUS_PERMITEM_CONCLUIR: CartaStatus[] = [
  'CONCORDANTE', 'DIVERGENTE', 'PARCIAL', 'INCONCLUSIVA', 'RECUSA', 'PROCEDIMENTO_ALTERNATIVO',
];

const CartaDetalheModal: React.FC<Props> = ({ registro, onFechar }) => {
  const [carregando, setCarregando] = useState(true);
  const [carta, setCarta] = useState<ICartaCircularizacao | undefined>();
  const [aprovando, setAprovando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [mostrarConcluir, setMostrarConcluir] = useState(false);
  const [justificativaConclusao, setJustificativaConclusao] = useState('');
  const [concluindo, setConcluindo] = useState(false);
  const [mensagem, setMensagem] = useState('');

  const carregar = async () => {
    setCarregando(true);
    const atual = await CircularizacaoService.obterCarta(registro.id);
    setCarta(atual);
    setCarregando(false);
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registro.id]);

  const aprovar = async () => {
    if (!carta) return;
    setAprovando(true);
    const resultado = await CircularizacaoService.aprovarCarta(carta.id, true);
    setAprovando(false);
    setMensagem(resultado.mensagem);
    if (resultado.sucesso) await carregar();
  };

  const baixar = async () => {
    if (!carta) return;
    setBaixando(true);
    try {
      await CircularizacaoService.baixarDocumentoCarta(carta.id, carta.numeroCarta);
    } finally {
      setBaixando(false);
    }
  };

  const concluir = async () => {
    if (!carta || !justificativaConclusao.trim()) return;
    setConcluindo(true);
    const resultado = await CircularizacaoService.concluirCarta(carta.id, justificativaConclusao.trim());
    setConcluindo(false);
    setMensagem(resultado.mensagem);
    if (resultado.sucesso) {
      setMostrarConcluir(false);
      await carregar();
    }
  };

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
      <div className='circ-modal' style={{ maxWidth: 480 }}>
        <div className='circ-modal-header'>
          <h4>Carta de Circularização — {registro.razaoSocialExibicao}</h4>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onFechar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body'>
          {carregando && <div className='text-muted text-center py-3'>Carregando...</div>}

          {!carregando && !carta && (
            <div className='text-muted' style={{ fontSize: 13 }}>
              Nenhuma carta preparada ainda para este destinatário. Use "Preparar Carta" na listagem antes de continuar.
            </div>
          )}

          {!carregando && carta && (
            <>
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                <div><strong>Número:</strong> {carta.numeroCarta}</div>
                <div><strong>Status:</strong> {LABEL_STATUS[carta.status]}</div>
                {carta.aprovadaEm && <div><strong>Aprovada em:</strong> {new Date(carta.aprovadaEm).toLocaleString('pt-BR')}</div>}
              </div>

              <div className='d-flex flex-wrap gap-2'>
                {carta.storageKey && (
                  <button type='button' className='btn btn-sm btn-light' disabled={baixando} onClick={baixar}>
                    {baixando && <span className='spinner-border spinner-border-sm me-1' />}
                    <i className='bi bi-file-earmark-arrow-down me-1' /> Baixar PDF
                  </button>
                )}

                {carta.status === 'PRONTA' && (
                  <button type='button' className='btn btn-sm btn-primary' disabled={aprovando} onClick={aprovar}>
                    {aprovando && <span className='spinner-border spinner-border-sm me-1' />}
                    <i className='bi bi-send-check me-1' /> Aprovar e Agendar Envio
                  </button>
                )}

                {STATUS_PERMITEM_CONCLUIR.includes(carta.status) && !mostrarConcluir && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-success'
                    onClick={() => {
                      setJustificativaConclusao(justificativaPadraoConclusao(carta.status));
                      setMostrarConcluir(true);
                    }}
                  >
                    <i className='bi bi-check-circle me-1' /> Concluir Procedimento
                  </button>
                )}
              </div>

              {mostrarConcluir && (
                <div className='circ-form-group mt-3'>
                  <label className='circ-campo-obrigatorio'>Justificativa da conclusão</label>
                  <textarea
                    className='circ-form-control'
                    rows={3}
                    value={justificativaConclusao}
                    onChange={(e) => setJustificativaConclusao(e.target.value)}
                    placeholder='Ex.: Resposta concordante recebida e validada — saldo confirmado sem divergências.'
                  />
                  <div className='d-flex gap-2 mt-2'>
                    <button type='button' className='btn btn-sm btn-light' onClick={() => setMostrarConcluir(false)}>
                      Cancelar
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      disabled={concluindo || !justificativaConclusao.trim()}
                      onClick={concluir}
                    >
                      {concluindo && <span className='spinner-border spinner-border-sm me-1' />}
                      Confirmar Conclusão
                    </button>
                  </div>
                </div>
              )}

              {mensagem && <div className='mt-3' style={{ fontSize: 12.5, color: '#47586b' }}>{mensagem}</div>}
            </>
          )}
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartaDetalheModal;
